import PropTypes from 'prop-types';
import React from 'react';
import { Query } from 'react-apollo';
import { BasePage, Helmet } from '@sm/webassets';
import { Grid, Row, Col } from '@sm/wds-react';

import MainHeading from '../../components/MainHeading';
import PricingNavigation from '../../components/PricingNavigation';
import PricingPackage from '../../components/Package';

import { getDefaultExperience } from '../../package-prices.graphql';

const MONTHLY_PACKAGES = [
  'STANDARD_MONTHLY',
  'ADVANTAGE_MONTHLY',
  'PREMIER_MONTHLY'
];
const INDIVIDUAL_PACKAGES = [
  'STANDARD_ANNUAL',
  'ADVANTAGE_ANNUAL',
  'PREMIER_ANNUAL'
];
const TEAMS_PACKAGES = [
  'TEAM_ADVANTAGE_ANNUAL',
  'TEAM_PREMIER_ANNUAL',
  'ENTERPRISE_PLATINUM'
];
// const SUITE_PRICING_PACKAGES = [
//   'ADVANTAGE_ANNUAL',
//   'TEAM_ADVANTAGE_ANNUAL',
//   'TEAM_PREMIER_ANNUAL'
// ];

const defaultPackagesFilter = (packageList = []) => {
  return pricingPackage => {
    return packageList.includes(pricingPackage.package);
  };
};

const renderFooter = packages => {
  return (
    <footer>
      <ul className="package-footnotes">
        <li>
          {`*Displayed pricing represents a 20% or more savings per user per month`}
          {`compared to individual Advantage and Premier plans`}
        </li>
        <li>
          **Add-on features available with Enterprise plan must be purchased
          separately
        </li>
      </ul>
      <a href="/pricing/details/">SEE ALL PLAN FEATURES</a>
      <h3>More about plans and pricing</h3>
      <dl>
        <dt>How much does SurveyMonkey cost?</dt>
        {packages.map(({ package: packageName, details, skuCost }) => {
          if (packageName === 'BASIC') {
            return <dd>{`The ${details.displayName} plan is FREE`}</dd>;
          }

          const coreSeatCost = skuCost.find(
            sku => sku.skuType === 'SUBSCRIPTION_CORE_SEAT'
          );
          let cost = coreSeatCost.cost / 100;
          let term = '';
          if (TEAMS_PACKAGES.includes(packageName)) {
            term += ', per user';
          }

          if (!MONTHLY_PACKAGES.includes(packageName)) {
            cost /= 12;
            term += ', billed annually';
          }

          return (
            <dd key={`plan-price-${packageName}`}>
              {`The ${details.displayName} plan costs $${cost} a month${term}.`}
            </dd>
          );
        })}
        <dt>Do you offer special pricing for students or educations?</dt>
        <dd>
          Yes! We offer discounted pricing for students and educators.
          <a href="#">See pricing</a>
        </dd>
        <dt>Do you offer discounts for teams?</dt>
        <dd>
          We offer discounts when you sign up for team plans that include 3 or
          more people. With Team Advantage and Team Premier plans, you’ll save
          20% or more over individual plans and get access to additional
          collaboration features.
          <a href="#">See pricing</a>
        </dd>
        <dt>Do you offer plans for corporate needs?</dt>
        <dd>
          Our Enterprise plans offer HIPAA compliance, advanced security, and
          admin features for increased governance and control.
          <a href="#">Contact sales</a>
        </dd>
      </dl>
    </footer>
  );
};

const renderPricingSummary = (packages, packageSet) => {
  const packagesFilter = defaultPackagesFilter(packageSet);

  return (
    <div
      className="packages wds-flex wds-flex--between wds-flex--stretch"
      key="packageList"
    >
      {packages.filter(packagesFilter).map(pricingPackage => (
        <PricingPackage
          {...pricingPackage}
          key={`package-${pricingPackage.package}`}
        />
      ))}
    </div>
  );
};

const PricingSummary = ({ packageTypes = 'teams' }) => {
  const packageSet =
    packageTypes === 'individual' ? INDIVIDUAL_PACKAGES : TEAMS_PACKAGES;

  return (
    <BasePage pageId="Pricing Summary" legacyWeb="pricingweb">
      <Helmet>
        <base target="_top" href="/" />
        <title>SurveyMonkey Plans and Pricing</title>
        <meta
          name="keywords"
          content="questionnaire, questionnaires, questionaire, questionaires, free online survey, free online surveys"
        />
        <meta
          name="description"
          content="Create and publish online surveys in minutes, and view results graphically and in real time. SurveyMonkey provides free online questionnaire and survey software."
        />
      </Helmet>
      <Grid data-testid="PricingSummary">
        <Row>
          <Col>
            <main>
              <header>
                <MainHeading />
                <nav>
                  <PricingNavigation />
                </nav>
              </header>
              <Query
                query={getDefaultExperience}
                variables={{
                  locale: 'US',
                  language: 'en',
                  experienceName: 'default'
                }}
              >
                {({ loading, error, data }) => (
                  <Choose>
                    <When condition={error}>
                      {/* TODO: Real error state */}
                      <h1>Derp!</h1>
                    </When>
                    <When condition={loading}>
                      {/* TODO: This will never been seen in a server side rendered context. Probably change up the conditions? */}
                      <h1>Loading</h1>
                    </When>
                    <Otherwise>
                      {renderPricingSummary(
                        data.pricingExperience.package,
                        packageSet
                      )}
                      {renderFooter(data.pricingExperience.package)}
                    </Otherwise>
                  </Choose>
                )}
              </Query>
            </main>
          </Col>
        </Row>
      </Grid>
    </BasePage>
  );
};

PricingSummary.defaultProps = {
  packageTypes: 'teams'
};

PricingSummary.propTypes = {
  packageTypes: PropTypes.string
};

export default PricingSummary;
